export const HEADER_PROLIX_APPLICATION_ERROR = 'x-prolix-application-error';
export const HEADER_TRACE_ID = 'x-trace-id';

export type WebshopFieldError = {
  field?: string | null;
  code?: string | null;
  message: string;
};

export type WebshopApiErrorPayload = {
  code?: string | null;
  message?: string | null;
  prolixErrorIdentifier?: string | null;
  rootCauseMessage?: string | null;
  fullStackTrace?: string | null;
  errors?: WebshopFieldError[] | null;
};

export type WebshopApiError = {
  status: number;
  isProlixApplicationError: boolean;
  traceId?: string;
  url?: string;
  payload?: WebshopApiErrorPayload;
  message: string;
};

export class WebshopApiErrorException extends Error {
  public readonly apiError: WebshopApiError;

  constructor(apiError: WebshopApiError) {
    super(apiError.message);
    this.name = 'WebshopApiErrorException';
    this.apiError = apiError;
  }
}

export function isWebshopApiErrorException(value: unknown): value is WebshopApiErrorException {
  return value instanceof WebshopApiErrorException;
}

export function isWebshopApiErrorPayload(value: unknown): value is WebshopApiErrorPayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    'code' in v ||
    'message' in v ||
    'prolixErrorIdentifier' in v ||
    'errors' in v
  );
}

export async function toWebshopApiError(
  response: Response,
  defaultMessage: string,
  url?: string
): Promise<WebshopApiError> {
  const status = response.status;
  const isProlixApplicationError = response.headers.get(HEADER_PROLIX_APPLICATION_ERROR) === 'true';
  const headerTraceId = response.headers.get(HEADER_TRACE_ID) ?? undefined;

  let payload: WebshopApiErrorPayload | undefined;
  let bodyTraceId: string | undefined;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      const body = await response.clone().json();
      if (isWebshopApiErrorPayload(body)) {
        payload = body as WebshopApiErrorPayload;
      }
      const candidate = (body as { traceId?: unknown })?.traceId;
      if (typeof candidate === 'string' && candidate.length > 0) {
        bodyTraceId = candidate;
      }
    } catch {
      // ignore
    }
  }

  const traceId = headerTraceId ?? bodyTraceId;
  const message = payload?.message ?? defaultMessage;

  return {
    status,
    isProlixApplicationError,
    traceId,
    url,
    payload,
    message
  };
}
